import { HRISProviderSlugEnum } from '@electricjs/core_entity-client';
import { User } from '@auth0/auth0-react';
import { getRequiredAddressValidationSchema, COUNTRIES } from '@electricjs/arc';

import type { Address } from '@/components/AddressGroupField';

import { getHrisProviderForConnectionName } from './connectionNameToHrisProvider';

function parseValidatedBillingAddress(address: Address): Address | undefined {
  const countryCode = address.country.trim().toUpperCase();
  if (!Object.hasOwn(COUNTRIES, countryCode)) {
    return undefined;
  }
  const normalized: Address = {
    ...address,
    country: countryCode,
  };
  try {
    getRequiredAddressValidationSchema().validateSync(normalized);
    return normalized;
  } catch {
    return undefined;
  }
}

export const CLAIM_EXTERNAL_COMPANY_ID =
  'https://my.electric.ai/external_company_id';
const CLAIM_EXTERNAL_COMPANY_NAME =
  'https://my.electric.ai/external_company_name';
const CLAIM_EXTERNAL_EMPLOYEE_FIRST_NAME =
  'https://my.electric.ai/external_employee_first_name';
const CLAIM_EXTERNAL_EMPLOYEE_LAST_NAME =
  'https://my.electric.ai/external_employee_last_name';
const CLAIM_EXTERNAL_EMPLOYEE_IS_ADMIN =
  'https://my.electric.ai/external_employee_is_admin';
export const CLAIM_EXTERNAL_CONNECTION_NAME =
  'https://my.electric.ai/external_connection_name';
const CLAIM_EXTERNAL_COMPANY_ADDRESS1 =
  'https://my.electric.ai/external_company_address1';
const CLAIM_EXTERNAL_COMPANY_ADDRESS2 =
  'https://my.electric.ai/external_company_address2';
const CLAIM_EXTERNAL_COMPANY_CITY =
  'https://my.electric.ai/external_company_city';
const CLAIM_EXTERNAL_COMPANY_STATE =
  'https://my.electric.ai/external_company_state';
const CLAIM_EXTERNAL_COMPANY_ZIP_CODE =
  'https://my.electric.ai/external_company_zip_code';
const CLAIM_EXTERNAL_COMPANY_COUNTRY =
  'https://my.electric.ai/external_company_country';
export const CLAIM_EXTERNAL_TENANT_ID =
  'https://my.electric.ai/external_tenant_id';

export type Auth0UserWithEmbeddedClaims = User & {
  [CLAIM_EXTERNAL_COMPANY_ID]?: string;
  [CLAIM_EXTERNAL_COMPANY_NAME]?: string;
  [CLAIM_EXTERNAL_EMPLOYEE_FIRST_NAME]?: string;
  [CLAIM_EXTERNAL_EMPLOYEE_LAST_NAME]?: string;
  [CLAIM_EXTERNAL_EMPLOYEE_IS_ADMIN]?: unknown;
  [CLAIM_EXTERNAL_CONNECTION_NAME]?: string;
  [CLAIM_EXTERNAL_COMPANY_ADDRESS1]?: string;
  [CLAIM_EXTERNAL_COMPANY_ADDRESS2]?: string;
  [CLAIM_EXTERNAL_COMPANY_CITY]?: string;
  [CLAIM_EXTERNAL_COMPANY_STATE]?: string;
  [CLAIM_EXTERNAL_COMPANY_ZIP_CODE]?: string;
  [CLAIM_EXTERNAL_COMPANY_COUNTRY]?: string;
  [CLAIM_EXTERNAL_TENANT_ID]?: string;
};

export type EmbeddedFirstLoginArgs = {
  externalCompanyId: string;
  externalCompanyName: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  hrisProviderSlug: HRISProviderSlugEnum;
  /** Company address from HRIS (e.g. TriNet HQ), when present. Used for billing. */
  address?: Address;
  /** Tenant id, when the connection requires one (currently isolved only). */
  externalTenantId?: string;
};

export function parseEmbeddedFirstLoginArgs(
  auth0User: Auth0UserWithEmbeddedClaims | null | undefined
): EmbeddedFirstLoginArgs | null {
  if (!auth0User) {
    return null;
  }
  const u = auth0User;
  const externalCompanyId = u[CLAIM_EXTERNAL_COMPANY_ID];
  const externalCompanyName = u[CLAIM_EXTERNAL_COMPANY_NAME];
  const email = u.email;
  const firstName = u[CLAIM_EXTERNAL_EMPLOYEE_FIRST_NAME];
  const lastName = u[CLAIM_EXTERNAL_EMPLOYEE_LAST_NAME];
  const isAdminClaim = u[CLAIM_EXTERNAL_EMPLOYEE_IS_ADMIN];
  const isAdmin = isAdminClaim === true || isAdminClaim === 'true';
  const connectionName = u[CLAIM_EXTERNAL_CONNECTION_NAME];
  const hrisProviderSlug =
    typeof connectionName === 'string' && connectionName
      ? getHrisProviderForConnectionName(connectionName)
      : undefined;

  const externalTenantId = u[CLAIM_EXTERNAL_TENANT_ID];
  // isolved's post-login Action rejects any /authorize request for that
  // connection without a tenantId, including the re-login fired after signup.
  const isMissingRequiredTenantId =
    hrisProviderSlug === HRISProviderSlugEnum.Isolved &&
    (typeof externalTenantId !== 'string' || !externalTenantId);

  if (
    typeof externalCompanyId !== 'string' ||
    !externalCompanyId ||
    typeof externalCompanyName !== 'string' ||
    !externalCompanyName ||
    typeof email !== 'string' ||
    !email ||
    !firstName ||
    !lastName ||
    !hrisProviderSlug ||
    isMissingRequiredTenantId
  ) {
    return null;
  }

  const address1 = u[CLAIM_EXTERNAL_COMPANY_ADDRESS1];
  const city = u[CLAIM_EXTERNAL_COMPANY_CITY];
  const state = u[CLAIM_EXTERNAL_COMPANY_STATE];
  const zip = u[CLAIM_EXTERNAL_COMPANY_ZIP_CODE];
  const country = u[CLAIM_EXTERNAL_COMPANY_COUNTRY];
  const hasRequiredAddressFields =
    typeof address1 === 'string' &&
    address1.trim() !== '' &&
    typeof city === 'string' &&
    city.trim() !== '' &&
    typeof state === 'string' &&
    state.trim() !== '' &&
    typeof zip === 'string' &&
    zip.trim() !== '' &&
    typeof country === 'string' &&
    country.trim() !== '';
  const address2 = u[CLAIM_EXTERNAL_COMPANY_ADDRESS2];
  const addressCandidate: Address | undefined = hasRequiredAddressFields
    ? {
        streetAddress1: address1.trim(),
        streetAddress2:
          typeof address2 === 'string' && address2.trim() !== ''
            ? address2.trim()
            : undefined,
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim(),
      }
    : undefined;
  const address = addressCandidate
    ? parseValidatedBillingAddress(addressCandidate)
    : undefined;

  return {
    externalCompanyId,
    externalCompanyName,
    firstName,
    lastName,
    email,
    isAdmin,
    hrisProviderSlug,
    ...(address && { address }),
    ...(typeof externalTenantId === 'string' &&
      externalTenantId && { externalTenantId }),
  };
}
